import BaseObject from "@shared/BaseObject.js";
import {LEGEND_HOVER_DIM_CLASS} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramLegendHoverHelper";
import {
	getSexualCommunicationLabel,
	hasSexualCommunicationValue,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramSexualCommunicationHelper";

const SEXUAL_COMMUNICATION_BADGE_OFFSET_Y = 10
const SEXUAL_COMMUNICATION_BADGE_OFFSET_X = 24
const SEXUAL_COMMUNICATION_BADGE_PADDING_X = 5
const SEXUAL_COMMUNICATION_BADGE_PADDING_Y = 3
const SEXUAL_COMMUNICATION_BADGE_BACKGROUND = "rgba(255, 255, 255, 0.8)"
const SEXUAL_COMMUNICATION_BADGE_TEXT_COLOR = "#111827"
const SEXUAL_COMMUNICATION_DIM_OPACITY_MULTIPLIER = 0.35

export default class GenogramSexualCommunicationBadgeRenderer extends BaseObject {

	constructor({cy}) {
		super()
		this.cy = cy
		this.layer = null
		this.animationFrameId = null
		this._listeners = {
			nodeGeometryChanged: this.requestUpdate.bind(this),
			nodeResized: this.requestUpdate.bind(this),
			dataChanged: this.requestUpdate.bind(this),
			nodeClassChanged: this.requestUpdate.bind(this),
			viewportChanged: this.requestUpdate.bind(this),
		}
		this.ensureLayer()
		this.addListeners()
	}

	addListeners() {
		this.cy.on("position add remove", "node", this._listeners.nodeGeometryChanged)
		this.cy.on("cyeditor.noderesize-resizing cyeditor.noderesize-resized", this._listeners.nodeResized)
		this.cy.on("cyeditor.data", this._listeners.dataChanged)
		this.cy.on("class", "node", this._listeners.nodeClassChanged)
		this.cy.on("pan zoom resize", this._listeners.viewportChanged)
	}

	ensureLayer() {
		if(this.layer || !this.cy.layers){
			return
		}
		this.layer = this.cy.layers().nodeLayer.insertAfter("canvas")
		this.layer.callback((ctx) => {
			this.renderIntoContext(ctx)
		})
	}

	update() {
		this.layer?.update()
	}

	requestUpdate() {
		if(this.animationFrameId){
			return
		}
		if(typeof requestAnimationFrame !== "function"){
			this.update()
			return
		}
		this.animationFrameId = requestAnimationFrame(() => {
			this.animationFrameId = null
			this.update()
		})
	}

	destroy() {
		this.cy.off("position add remove", "node", this._listeners.nodeGeometryChanged)
		this.cy.off("cyeditor.noderesize-resizing cyeditor.noderesize-resized", this._listeners.nodeResized)
		this.cy.off("cyeditor.data", this._listeners.dataChanged)
		this.cy.off("class", "node", this._listeners.nodeClassChanged)
		this.cy.off("pan zoom resize", this._listeners.viewportChanged)
		if(this.animationFrameId && typeof cancelAnimationFrame === "function"){
			cancelAnimationFrame(this.animationFrameId)
			this.animationFrameId = null
		}
		this.layer?.remove()
		this.layer = null
	}

	renderIntoContext(ctx) {
		let nodes = this.getRenderableNodes()
		for(let node of nodes){
			this.drawBadge(ctx, node)
		}
	}

	getRenderableNodes() {
		return this.cy.nodes().filter((node) => this.isRenderableNode(node))
	}

	isRenderableNode(node) {
		if(!node?.isNode()){
			return false
		}
		if(node.data("isAuxNode") || node.data("tempElement")){
			return false
		}
		if(node.hasClass("eh-handle") || node.hasClass("eh-ghost-node")){
			return false
		}
		return hasSexualCommunicationValue(node.data("sexualCommunication"))
	}

	drawBadge(ctx, node) {
		let label = getSexualCommunicationLabel(node.data("sexualCommunication"))
		if(!label){
			return
		}

		let box = node.boundingBox({
			includeLabels: false,
			includeOverlays: false,
		})
		let fontSize = this.resolveFontSize(box)
		let badgeMetrics = this.measureBadge(ctx, label, fontSize)
		let badgeX = box.x1 - SEXUAL_COMMUNICATION_BADGE_OFFSET_X
		let badgeBottomY = box.y1 - SEXUAL_COMMUNICATION_BADGE_OFFSET_Y
		let badgeY = badgeBottomY - badgeMetrics.height

		ctx.save()
		if(node.hasClass(LEGEND_HOVER_DIM_CLASS)){
			ctx.globalAlpha *= SEXUAL_COMMUNICATION_DIM_OPACITY_MULTIPLIER
		}
		ctx.font = `${fontSize}px Helvetica`
		ctx.textAlign = "left"
		ctx.textBaseline = "top"
		ctx.fillStyle = SEXUAL_COMMUNICATION_BADGE_BACKGROUND
		ctx.fillRect(badgeX, badgeY, badgeMetrics.width, badgeMetrics.height)
		ctx.fillStyle = SEXUAL_COMMUNICATION_BADGE_TEXT_COLOR
		ctx.fillText(label, badgeX + SEXUAL_COMMUNICATION_BADGE_PADDING_X, badgeY + SEXUAL_COMMUNICATION_BADGE_PADDING_Y)
		ctx.restore()
	}

	resolveFontSize(box) {
		let badgeFontSize = Math.max(12, Math.round(Math.min(box.w, box.h) * 0.28))
		return Math.min(30, badgeFontSize)
	}

	measureBadge(ctx, label, fontSize) {
		ctx.save()
		ctx.font = `${fontSize}px Helvetica`
		let textWidth = ctx.measureText(label).width
		ctx.restore()
		return {
			width: textWidth + 2 * SEXUAL_COMMUNICATION_BADGE_PADDING_X,
			height: fontSize + 2 * SEXUAL_COMMUNICATION_BADGE_PADDING_Y,
		}
	}
}
